import { Card } from "@/components/layout";
import {
  MetricKpiTile,
  PanelError,
  PanelLoading,
  SectionHeading,
  panelErrorTitle,
} from "@/components/dashboard";
import { HorizontalBarList } from "@/components/charts";
import { useReservationUsers } from "@/hooks/useReservationUsers";
import type { TimeRange } from "@/lib/time-range";

interface Props {
  range: TimeRange;
}

const int = (n: number | undefined) =>
  n === undefined ? "—" : Math.round(n).toLocaleString("en-US");

/**
 * Who actually books — distinct users who completed a reservation, how many
 * reservations they make on average, and how many come back to book again.
 * Derived from LAW `reservation_submitted` events keyed by `accountId`.
 */
export function ReservationUsersSection({ range }: Props) {
  const q = useReservationUsers(range);
  const d = q.data;
  const err = q.error ? panelErrorTitle(q.error) : undefined;

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="Reservations by user"
        source="log-analytics"
        description="How many distinct customers actually booked, how many reservations each makes, and the repeat-booking rate. Counts unique accounts on submitted reservations — guest bookings with no account are shown separately."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricKpiTile
          label="Distinct reservers"
          value={int(d?.distinctUsers)}
          delta={d ? `${int(d.identifiedReservations)} reservations from accounts` : undefined}
          tone="emerald"
          hint="Unique customer accounts that submitted at least one reservation in this range (dcount of accountId on reservation_submitted)."
          isLoading={q.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="Total reservations"
          value={int(d?.totalReservations)}
          delta={d ? `${int(d.anonymousReservations)} without an account` : undefined}
          tone="sky"
          hint="Every submitted reservation. 'Without an account' are guest/unauthenticated bookings that carry no accountId, so they can't be attributed to a distinct user."
          isLoading={q.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="Avg reservations / user"
          value={d ? d.avgReservationsPerUser.toFixed(2) : "—"}
          delta="account-attributed bookings"
          tone="neutral"
          hint="Identified reservations ÷ distinct reservers. Above 1.0 means customers book more than once in the range."
          isLoading={q.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="Repeat reservers"
          value={d ? `${d.repeatReserverRate.toFixed(1)}%` : "—"}
          delta="users with 2+ reservations"
          tone={d ? (d.repeatReserverRate >= 25 ? "emerald" : d.repeatReserverRate >= 10 ? "amber" : "neutral") : "neutral"}
          hint="Share of distinct reservers who booked more than once — a proxy for loyalty / returning demand."
          isLoading={q.isLoading}
          errorTitle={err}
        />
      </div>

      <Card title="Users by number of reservations">
        {q.isLoading ? (
          <PanelLoading />
        ) : q.error ? (
          <PanelError error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <HorizontalBarList
            tone="sky"
            entries={(d?.distribution ?? []).map((b) => ({
              label: `${b.label} reservation${b.reservations === 1 ? "" : "s"}`,
              value: b.users,
            }))}
          />
        )}
      </Card>
    </section>
  );
}
